import {Form as InertiaForm, Link} from '@inertiajs/react'
import { type ComponentProps, useEffect, useState } from 'react'

import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Textarea } from '@/components/ui/textarea.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'

import { UserType } from './types.ts'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Save} from "lucide-react";

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  user: UserType
  submitText: string
}

const roleOptions = [
  { value: 'agent', label: 'Agent' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super admin' },
  { value: 'manager', label: 'Manager' },
] as const

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const

export default function Form({ user, submitText, ...formProps }: FormProps) {
  const initialRole = user.role || 'agent'
  const initialStatus = user.status || 'active'
  const [role, setRole] = useState(initialRole)
  const [status, setStatus] = useState(initialStatus)

  useEffect(() => {
    setRole(initialRole)
    setStatus(initialStatus)
  }, [initialRole, initialStatus])

  return (
      <InertiaForm
          transform={(data) => ({ user: data })}
          className="contents"
          {...formProps}
      >
        {({ errors, processing }) => (
            <>
              <div className="grid gap-6 md:grid-cols-2">
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle>Basic Information</CardTitle>
                    <CardDescription>Enter the user's basic details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="my-2">
                        <Label htmlFor="firstName">First name</Label>
                        <Input
                            type="text"
                            name="firstName"
                            id="firstName"
                            placeholder="First name"
                            defaultValue={user.firstName}
                        />
                        {errors.firstName && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.firstName.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="my-2">
                        <Label htmlFor="lastName">Last name</Label>
                        <Input
                            type="text"
                            name="lastName"
                            id="lastName"
                            placeholder="Last name"
                            defaultValue={user.lastName}
                        />
                        {errors.lastName && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.lastName.join(', ')}
                            </div>
                        )}
                      </div>
                      <div className="my-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            type="email"
                            name="email"
                            id="email"
                            placeholder="Email"
                            defaultValue={user.email}
                        />
                        {errors.email && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.email.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="my-2">
                        <Label htmlFor="contact">Contact</Label>
                        <Input
                            type="text"
                            name="contact"
                            id="contact"
                            placeholder="Contact"
                            defaultValue={user.contact}
                        />
                        {errors.contact && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.contact.join(', ')}
                            </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Role & Access</CardTitle>
                    <CardDescription>Set the user's role and community access</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="my-2">
                        <Label htmlFor="role">Role</Label>
                        <input type="hidden" name="role" value={role} />
                        <Select value={role} onValueChange={setRole}>
                          <SelectTrigger id="role" className={"w-full"}>
                            <SelectValue placeholder="Role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.role && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.role.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="my-2">
                        <Label htmlFor="status">Status</Label>
                        <input type="hidden" name="status" value={status} />
                        <Select value={status} onValueChange={setStatus}>
                          <SelectTrigger id="status" className={"w-full"}>
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.status && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.status.join(', ')}
                            </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Security</CardTitle>
                    <CardDescription>Set the initial password for this user</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">


                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="my-2">
                        <Label htmlFor="password">Password</Label>
                        <Input
                            type="password"
                            name="password"
                            id="password"
                            placeholder="Leave blank to keep current password"
                        />
                        {errors.password && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.password.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="my-2">
                        <Label htmlFor="password_confirmation">Confirm password</Label>
                        <Input
                            type="password"
                            name="password_confirmation"
                            id="password_confirmation"
                            placeholder="Confirm password"
                        />
                        {errors.password_confirmation && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.password_confirmation.join(', ')}
                            </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle>Additional Notes</CardTitle>
                    <CardDescription>Add any additional information about this user</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="my-2">
                      <Label htmlFor="note">Note</Label>
                      <Textarea
                          name="note"
                          id="note"
                          placeholder="Notes"
                          defaultValue={user.note}
                      />
                      {errors.note && (
                          <div className="text-red-500 px-3 py-2 font-medium">
                            {errors.note.join(', ')}
                          </div>
                      )}
                    </div>
                  </CardContent>
                </Card>













                <div/>
                <div className="mt-6 flex justify-end gap-4">
                  <Link href="/users">
                    <Button type="button" variant="outline">
                      Cancel
                    </Button>
                  </Link>
                  <Button disabled={processing} type="submit">
                    <Save className="mr-2 h-4 w-4" />
                    Create User
                  </Button>
                </div>
              </div>
            </>
        )}
      </InertiaForm>
  )
}